import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  api,
  ApiError,
  type ClubBookingRules,
  type ClubReservationInfo,
  type PlayerAvailabilityCourt,
  type PlayerClubBrief,
  type PlayerClubWeekUsage,
} from "../../lib/api";
import { useAbortableConfirm } from "../../lib/useAbortableConfirm";
import { formatDate } from "../../lib/dateFormat";
import { uploadAssetHref } from "../../lib/assets";
import T123MembershipCard from "../../components/T123MembershipCard";
import type { T123Standing } from "../../lib/t123MembershipApi";

// «Mi club» del jugador (maqueta Club-Jugador, etapa 2): membresía, días,
// horarios libres, reservas que le quedan en la semana y «Mis reservas».

export function localIso(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
}

function parseIso(dateIso: string): Date {
  const [year, month, day] = dateIso.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function dayChipLabel(dateIso: string, todayIso: string): { top: string; bottom: string } {
  const value = parseIso(dateIso);
  const weekday = value.toLocaleDateString("es-MX", { weekday: "short" }).replace(".", "");
  const day = value.getDate();
  if (dateIso === todayIso) return { top: "Hoy", bottom: `${weekday} ${day}` };
  return { top: weekday.charAt(0).toUpperCase() + weekday.slice(1), bottom: String(day) };
}

function longDateLabel(dateIso: string | null): string {
  if (!dateIso) return "—";
  const value = parseIso(dateIso);
  const text = value.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function minutesBetween(start: string, end: string): number {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return eh * 60 + em - (sh * 60 + sm);
}

function timeLabel(value: string): string {
  const [hour, minute] = value.split(":");
  return `${Number(hour)}:${minute}`;
}

export type TimeRow = {
  start: string;
  free: Array<{ courtId: number; courtName: string; end: string }>;
  state: "free" | "full" | "tournament";
};

/** Agrupa los horarios de todas las canchas por hora de inicio. */
export function groupSlotsByTime(courts: PlayerAvailabilityCourt[]): TimeRow[] {
  const rows = new Map<string, TimeRow & { tournament: boolean }>();
  courts.forEach((court) =>
    court.slots.forEach((slot) => {
      const row = rows.get(slot.start_time) ?? { start: slot.start_time, free: [], state: "full", tournament: false };
      if (slot.available) row.free.push({ courtId: court.id, courtName: court.name, end: slot.end_time });
      if (slot.reason === "tournament") row.tournament = true;
      rows.set(slot.start_time, row);
    }),
  );
  return Array.from(rows.values())
    .sort((a, b) => a.start.localeCompare(b.start))
    .map(({ tournament, ...row }) => ({
      ...row,
      state: row.free.length > 0 ? "free" : tournament ? "tournament" : "full",
    }));
}

function payerLine(club: PlayerClubBrief): string {
  const payer = club.membership?.payer;
  if (payer === "club") return "Tu club cubre tu membresía.";
  if (payer === "none") return "Este club no cobra membresía en la app.";
  return "Membresía: disponible pronto. Por ahora no se cobra nada en la app.";
}

const T123_OK_CODES = new Set(["active", "cancelled_active", "exempt", "club_pays", "billing_off"]);

const STATUS_LABELS: Record<string, string> = {
  active: "Confirmada",
  pending: "Por aprobar",
};

type Props = {
  token: string;
  club: PlayerClubBrief;
  profileId: number | null;
  onError: (message: string | null) => void;
  onMessage: (message: string | null) => void;
};

export default function PlayerMiClub({ token, club, profileId, onError, onMessage }: Props) {
  const { askConfirm } = useAbortableConfirm([club.id, profileId]);
  // Con la membresía Torneos123 encendida, el estado de pago lo dice su
  // tarjeta; arriba solo se muestra si es socio del club.
  const [t123Active, setT123Active] = useState(false);
  const [t123Standing, setT123Standing] = useState<T123Standing | null>(null);
  const todayIso = localIso(new Date());
  const days = useMemo(() => {
    const base = parseIso(todayIso);
    return Array.from({ length: 7 }, (_, index) => {
      const value = new Date(base);
      value.setDate(base.getDate() + index);
      return localIso(value);
    });
  }, [todayIso]);
  const [date, setDate] = useState(todayIso);
  const [courts, setCourts] = useState<PlayerAvailabilityCourt[]>([]);
  const [rules, setRules] = useState<ClubBookingRules | null>(null);
  const [weekUsage, setWeekUsage] = useState<PlayerClubWeekUsage | null>(null);
  const [confirmation, setConfirmation] = useState<string>("auto");
  const [reservations, setReservations] = useState<ClubReservationInfo[]>([]);
  const [picking, setPicking] = useState<TimeRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [availability, mine] = await Promise.all([
        api.clubs.playerAvailability(token, club.id, date, profileId),
        api.clubs.playerMyReservations(token, club.id, profileId),
      ]);
      setCourts(availability.data.courts);
      setRules(availability.data.rules ?? null);
      setWeekUsage(availability.data.week_usage ?? null);
      setConfirmation(availability.data.reservation_confirmation ?? "auto");
      setReservations(mine.data.reservations);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "No pudimos cargar los horarios del club.");
    } finally {
      setLoaded(true);
    }
  }, [club.id, date, onError, profileId, token]);

  useEffect(() => {
    setPicking(null);
    void refresh();
  }, [refresh]);

  const rows = useMemo(() => groupSlotsByTime(courts), [courts]);
  const upcoming = useMemo(
    () =>
      reservations
        .filter((r) => (r.status === "active" || r.status === "pending") && (r.date ?? "") >= todayIso)
        .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`)),
    [reservations, todayIso],
  );

  async function book(courtId: number, courtName: string, start: string) {
    const confirmed = await askConfirm(
      `¿Reservar ${courtName} el ${longDateLabel(date)} a las ${timeLabel(start)}?`,
      { acceptLabel: "Sí, reservar" },
    );
    if (!confirmed) return;
    setBusy(true);
    onError(null);
    onMessage(null);
    try {
      const resp = await api.clubs.playerBook(token, club.id, { court_id: courtId, date, start_time: start }, profileId);
      onMessage(
        resp.data.reservation.status === "pending"
          ? `Reserva enviada: ${courtName} a las ${timeLabel(start)}. El club la tiene que aprobar.`
          : `Reserva confirmada: ${courtName} a las ${timeLabel(start)}.`,
      );
      setPicking(null);
      await refresh();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "No se pudo reservar.");
    } finally {
      setBusy(false);
    }
  }

  function chooseTime(row: TimeRow) {
    if (row.state !== "free" || busy) return;
    if (row.free.length === 1) {
      void book(row.free[0].courtId, row.free[0].courtName, row.start);
      return;
    }
    setPicking(picking?.start === row.start ? null : row);
  }

  async function cancel(reservation: ClubReservationInfo) {
    const confirmed = await askConfirm(
      `¿Cancelar tu reserva del ${longDateLabel(reservation.date)} a las ${timeLabel(reservation.start_time)}?`,
      { acceptLabel: "Sí, cancelar mi reserva", tone: "danger" },
    );
    if (!confirmed) return;
    setBusy(true);
    onError(null);
    onMessage(null);
    try {
      const resp = await api.clubs.playerCancelReservation(token, reservation.id);
      onMessage(
        resp.data.late
          ? "Reserva cancelada tarde: cuenta dentro de tus reservas de la semana."
          : "Reserva cancelada.",
      );
      await refresh();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "No se pudo cancelar.");
    } finally {
      setBusy(false);
    }
  }

  const membership = club.membership;
  const suspended = membership?.status && membership.status !== "active";
  // Con cobro encendido y la membresía Torneos123 sin estar al corriente, el
  // encabezado habla de la afiliación al club y muestra el estado del cobro.
  const t123NotPaid = t123Active && t123Standing != null && !T123_OK_CODES.has(t123Standing.code);
  const credentialUrl = uploadAssetHref(club.credential_url);
  const weekText =
    weekUsage?.remaining != null
      ? weekUsage.remaining === 1
        ? "Te queda 1 reserva esta semana"
        : `Te quedan ${weekUsage.remaining} reservas esta semana`
      : "Sin límite de reservas por semana";
  const advanceText = rules?.max_advance_days != null ? ` · puedes reservar hasta con ${rules.max_advance_days} días.` : ".";

  return (
    <>
      <section className="mi-club__card mi-club__membership" aria-label="Tu membresía">
        <p className="mi-club__eyebrow">Tu membresía</p>
        <h2 className={suspended ? "mi-club__status mi-club__status--warn" : "mi-club__status"}>
          {suspended ? "Suspendida" : t123NotPaid ? "Socio del club" : t123Active ? "Socio activo" : "Al corriente"}
        </h2>
        {!suspended && t123NotPaid ? (
          <p className="mi-club__chip">Membresía Torneos123: {t123Standing?.label}</p>
        ) : null}
        <p className="mi-club__line">
          {suspended
            ? "Acércate a la recepción del club."
            : t123NotPaid
              ? "Afiliación al club vigente"
              : t123Active
                ? "Socio del club"
                : "Socio activo"}
          {membership?.joined_at ? ` · desde ${formatDate(membership.joined_at)}` : ""}
        </p>
        <T123MembershipCard
          token={token}
          clubId={club.id}
          playerProfileId={profileId}
          fallback={<p className="mi-club__line mi-club__line--soft">{payerLine(club)}</p>}
          onBillingActive={setT123Active}
          onStanding={setT123Standing}
        />
        {credentialUrl ? (
          <Link
            className="mi-club__link"
            to={`/player/documento?${new URLSearchParams({ src: credentialUrl, label: "Credencial", back: "/player/club" }).toString()}`}
          >
            Ver mi credencial ›
          </Link>
        ) : null}
      </section>

      <section className="mi-club__card" aria-label="Reservar cancha">
        <h2 className="mi-club__title">Reservar cancha</h2>
        <div className="mi-club__days" role="group" aria-label="Día">
          {days.map((day) => {
            const label = dayChipLabel(day, todayIso);
            return (
              <button
                key={day}
                type="button"
                className="mi-club__day"
                aria-pressed={day === date}
                disabled={busy}
                onClick={() => setDate(day)}
              >
                <strong>{label.top}</strong>
                <span>{label.bottom}</span>
              </button>
            );
          })}
        </div>
        <p className="mi-club__hint">
          Horarios libres {date === todayIso ? "hoy" : `el ${longDateLabel(date)}`} · toca uno
        </p>
        {!loaded ? (
          <p className="mi-club__hint">Cargando horarios…</p>
        ) : rows.length === 0 ? (
          <p className="mi-club__hint">El club no abre este día.</p>
        ) : (
          <div className="mi-club__slots">
            {rows.map((row) => (
              <button
                key={row.start}
                type="button"
                className={`mi-club__slot mi-club__slot--${row.state}`}
                aria-pressed={picking?.start === row.start}
                disabled={row.state !== "free" || busy}
                onClick={() => chooseTime(row)}
              >
                <strong>{timeLabel(row.start)}</strong>
                <span>
                  {row.state === "free"
                    ? `${row.free.length} ${row.free.length === 1 ? "cancha" : "canchas"}`
                    : row.state === "tournament"
                      ? "torneo"
                      : "lleno"}
                </span>
              </button>
            ))}
          </div>
        )}
        {picking ? (
          <div className="mi-club__picker" role="group" aria-label={`Canchas libres a las ${timeLabel(picking.start)}`}>
            <p className="mi-club__hint">Elige cancha para las {timeLabel(picking.start)}:</p>
            <div className="mi-club__picker-list">
              {picking.free.map((free) => (
                <button
                  key={free.courtId}
                  type="button"
                  className="mi-club__court"
                  disabled={busy}
                  onClick={() => void book(free.courtId, free.courtName, picking.start)}
                >
                  {free.courtName}
                  <small>{minutesBetween(picking.start, free.end)} min</small>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <p className="mi-club__note">
          {weekText}
          {advanceText}
          {confirmation === "club_approves" ? " El club aprueba cada reserva." : ""}
        </p>
      </section>

      <section className="mi-club__card" aria-label="Mis reservas">
        <h2 className="mi-club__title">Mis reservas</h2>
        {upcoming.length === 0 ? (
          <p className="mi-club__hint">No tienes reservas próximas.</p>
        ) : (
          <ul className="mi-club__reservations">
            {upcoming.map((reservation) => (
              <li key={reservation.id} className="mi-club__reservation">
                <div className="mi-club__reservation-info">
                  <strong>
                    {longDateLabel(reservation.date)} · {timeLabel(reservation.start_time)}
                  </strong>
                  <span>
                    {reservation.court_name ?? "Cancha"} · {minutesBetween(reservation.start_time, reservation.end_time)} min
                    {" · "}
                    <em className={`mi-club__pill mi-club__pill--${reservation.status}`}>
                      {STATUS_LABELS[reservation.status] ?? reservation.status}
                    </em>
                  </span>
                </div>
                <button
                  type="button"
                  className="mi-club__cancel"
                  disabled={busy}
                  onClick={() => void cancel(reservation)}
                >
                  Cancelar
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="mi-club__note">
          {rules?.cancel_hours != null
            ? `Cancela sin penalización hasta ${rules.cancel_hours} ${rules.cancel_hours === 1 ? "hora" : "horas"} antes.`
            : "Puedes cancelar sin penalización en cualquier momento."}
        </p>
      </section>
    </>
  );
}
